import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';

export type PaymentCardVariant = 'full' | 'drawer' | 'compact' | 'custom';

export interface PaymentCardField {
  label: string;
  value: any;
  isAmount?: boolean;
  isDate?: boolean;
  tooltip?: string;
  flex?: string | number;
  class?: string;
}

@Component({
  selector: 'app-payment-card',
  templateUrl: './app-payment-card.component.html',
  styleUrls: ['./app-payment-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppPaymentCardComponent {
  /** The transaction or payment data object */
  @Input() payment: any;

  /** Layout variant: 'full' (customer/list view) | 'drawer' / 'compact' (modal/drawer view) | 'custom' */
  @Input() variant: PaymentCardVariant = 'full';

  /** Explicit field overrides (falls back to payment object properties if omitted) */
  @Input() tranId?: string | number;
  @Input() paidDate?: string | Date;
  @Input() customerName?: string;
  @Input() invoiceNumber?: string;
  @Input() amount?: string | number;
  @Input() method?: string;
  @Input() paymentRef?: string;
  @Input() isDeleted?: boolean;
  @Input() status?: number | string;

  /** Custom fields list when using dynamic columns */
  @Input() customFields?: PaymentCardField[];

  /** Permission name for *appHasAccess check */
  @Input() accessPermission: string = 'SALES_CONTACTS';

  /** Permissions & action visibility */
  @Input() showEditAction: boolean = true;
  @Input() showDeleteAction: boolean = true;
  @Input() showActions: boolean = true;

  /** Custom class for outer wrapper */
  @Input() customClass: string = '';
  @Input() clickable: boolean = true;

  /** Event emitters */
  @Output() cardClick = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();

  get resolvedTranId(): string | number {
    return this.tranId !== undefined ? this.tranId : (this.payment?.iqp_tran_id || this.payment?.tranId || '-');
  }

  get resolvedPaidDate(): string | Date {
    return this.paidDate !== undefined ? this.paidDate : (this.payment?.iqp_paid_date || this.payment?.paid_date || this.payment?.date || '');
  }

  get resolvedCustomerName(): string {
    const raw = this.customerName !== undefined 
      ? this.customerName 
      : (this.payment?.contact_first_name || this.payment?.contact_business_name || this.payment?.customer_name || '-');
    return raw;
  }

  get customerNameDisplay(): string {
    const name = this.resolvedCustomerName;
    if (name && name.length > 20) {
      return name.substring(0, 20) + '...';
    }
    return name || '-';
  }

  get resolvedInvoiceNumber(): string {
    const raw = this.invoiceNumber !== undefined 
      ? this.invoiceNumber 
      : (this.payment?.iqp_invoice_number || this.payment?.invoice_number || '-');
    return raw;
  }

  get invoiceNumberDisplay(): string {
    const num = this.resolvedInvoiceNumber;
    if (num && num.length > 25) {
      return num.substring(0, 25) + '...';
    }
    return num || '-';
  }

  get resolvedAmount(): any {
    return this.amount !== undefined 
      ? this.amount 
      : (this.payment?.iqp_partly_pmnt_amnt ?? this.payment?.amount ?? this.payment?.tran_amount ?? 0);
  }

  get resolvedMethod(): string {
    return this.method !== undefined 
      ? this.method 
      : (this.payment?.iqp_payment_type || this.payment?.payment_type || this.payment?.method || '-');
  }

  get resolvedPaymentRef(): string {
    return this.paymentRef !== undefined 
      ? this.paymentRef 
      : (this.payment?.iqp_payment_ref || this.payment?.payment_ref || '-');
  }

  get isCardDeleted(): boolean {
    return this.isDeleted !== undefined ? !!this.isDeleted : (!!this.payment?.is_deleted || !!this.payment?.trx_deleted);
  }

  get trxStatus(): number | string | undefined {
    return this.status !== undefined ? this.status : this.payment?.trx_status;
  }

  get isVerified(): boolean {
    return !this.isCardDeleted && (this.trxStatus === 101 || this.trxStatus === 'verified' || this.trxStatus === 'Verified');
  }

  get isDisputed(): boolean {
    return !this.isCardDeleted && (this.trxStatus === 102 || this.trxStatus === 'disputed' || this.trxStatus === 'Disputed');
  }

  get canShowActions(): boolean {
    return this.showActions && !this.isCardDeleted && !this.isVerified;
  }

  onCardClick(event: MouseEvent): void {
    if (this.clickable) {
      this.cardClick.emit(this.payment);
    }
  }

  onEdit(event?: any): void {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    this.edit.emit(this.payment);
  }

  onDelete(event?: any): void {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    this.delete.emit(this.payment);
  }
}
